import React, { useState } from "react";
import {
    Alert,
    Button,
    Card,
    Input,
    Modal,
    Popconfirm,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
    message,
} from "antd";
import {
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    FileTextOutlined,
    GlobalOutlined,
    InfoCircleOutlined,
    ReloadOutlined,
    ThunderboltOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../hooks/useAuth";
import { useIntl } from "react-intl";
import {
    useGetBrandKnowledgeDocumentsQuery,
    useUploadBrandKnowledgeDocumentMutation,
    useAddBrandKnowledgeScrapeMutation,
    useRescrapeBrandKnowledgeDocumentMutation,
    useDeleteBrandKnowledgeDocumentMutation,
    useLazyGetBrandKnowledgeDocumentDownloadUrlQuery,
    useLazyGetBrandKnowledgeDocumentTextQuery,
    useUpdateBrandKnowledgeDocumentTextMutation,
    useRedistillBrandKnowledgeDocumentMutation,
    useSetBrandKnowledgeDocumentIncludeInContextMutation,
} from "../../services/nestjs-base-api";
import { IBrandKnowledgeDocument } from "../../services/nestjs-base-api";

const { TextArea } = Input;

const { Title, Paragraph, Text } = Typography;

interface IProps {
    brandId: number;
    /** Called with the new document id after a successful upload/scrape. */
    onIngested?: (documentId: number) => void;
}

const ACCEPTED = ".txt,.md,.csv,.pdf,.xlsx,.docx";

/**
 * SYR-78 Phase 2 — documents & website scrapes for a brand's knowledge base.
 * Rendered under the Brand Knowledge tab.
 */
const BrandKnowledgeDocuments: React.FunctionComponent<IProps> = ({
    brandId,
    onIngested,
}) => {
    const { user } = useAuth();
    const intl = useIntl();
    const jwtToken = user?.jwtToken || "";
    const [url, setUrl] = useState("");

    const { data: documents = [], isLoading } =
        useGetBrandKnowledgeDocumentsQuery(
            { jwtToken, brandId },
            { skip: !jwtToken || !brandId }
        );

    const [uploadDoc, { isLoading: isUploading }] =
        useUploadBrandKnowledgeDocumentMutation();
    const [addScrape, { isLoading: isScraping }] =
        useAddBrandKnowledgeScrapeMutation();
    const [rescrape] = useRescrapeBrandKnowledgeDocumentMutation();
    const [deleteDoc] = useDeleteBrandKnowledgeDocumentMutation();
    const [triggerDownload] = useLazyGetBrandKnowledgeDocumentDownloadUrlQuery();
    const [fetchDocText] = useLazyGetBrandKnowledgeDocumentTextQuery();
    const [updateText, { isLoading: isSavingText }] =
        useUpdateBrandKnowledgeDocumentTextMutation();
    const [redistill] = useRedistillBrandKnowledgeDocumentMutation();
    const [setInclude] =
        useSetBrandKnowledgeDocumentIncludeInContextMutation();

    const handleToggleInclude = async (
        documentId: number,
        include: boolean
    ) => {
        try {
            await setInclude({
                jwtToken,
                brandId,
                documentId,
                include,
            }).unwrap();
        } catch {
            message.error(
                t("brandKnowledge.docs.toggleError", "Could not update")
            );
        }
    };

    const [editDoc, setEditDoc] = useState<{ id: number; ref: string } | null>(
        null
    );
    const [editText, setEditText] = useState("");
    const [loadingText, setLoadingText] = useState(false);

    const t = (id: string, defaultMessage: string) =>
        intl.formatMessage({ id, defaultMessage });

    const openEdit = async (doc: IBrandKnowledgeDocument) => {
        setEditDoc({ id: doc.id, ref: doc.sourceRef });
        setEditText("");
        setLoadingText(true);
        try {
            const data = await fetchDocText({
                jwtToken,
                brandId,
                documentId: doc.id,
            }).unwrap();
            setEditText(data.extractedText || "");
        } catch {
            message.error(
                t("brandKnowledge.docs.loadError", "Could not load the text")
            );
        } finally {
            setLoadingText(false);
        }
    };

    const saveEdit = async () => {
        if (!editDoc) return;
        try {
            await updateText({
                jwtToken,
                brandId,
                documentId: editDoc.id,
                text: editText,
            }).unwrap();
            message.success(t("brandKnowledge.docs.textSaved", "Text saved"));
            setEditDoc(null);
        } catch (err: any) {
            message.error(
                err?.data?.message ||
                    t("brandKnowledge.docs.saveTextError", "Could not save the text"),
                8
            );
        }
    };

    const handleRedistill = async (documentId: number) => {
        try {
            await redistill({ jwtToken, brandId, documentId }).unwrap();
            message.success(
                t("brandKnowledge.docs.redistilled", "Re-cleaned with AI")
            );
        } catch (err: any) {
            message.error(
                err?.data?.message ||
                    t("brandKnowledge.docs.redistillError", "Could not re-clean"),
                8
            );
        }
    };

    const handleUpload = async (file: File) => {
        try {
            const doc = await uploadDoc({ jwtToken, brandId, file }).unwrap();
            message.success(t("brandKnowledge.docs.uploaded", "Document added"));
            onIngested?.(doc.id);
        } catch (err: any) {
            message.error(
                err?.data?.message ||
                    t("brandKnowledge.docs.uploadError", "Could not add document"),
                8
            );
        }
        return false; // prevent antd's default upload
    };

    const handleAddUrl = async () => {
        if (!url.trim()) return;
        try {
            const doc = await addScrape({
                jwtToken,
                brandId,
                url: url.trim(),
            }).unwrap();
            message.success(t("brandKnowledge.docs.scraped", "Website added"));
            setUrl("");
            onIngested?.(doc.id);
        } catch (err: any) {
            // Backend returns a specific, actionable reason (bot protection, 404,
            // JS-rendered, etc.) — show it for long enough to read (8s).
            message.error(
                err?.data?.message ||
                    t("brandKnowledge.docs.scrapeError", "Could not scrape that URL"),
                8
            );
        }
    };

    const handleRescrape = async (documentId: number) => {
        try {
            await rescrape({ jwtToken, brandId, documentId }).unwrap();
            message.success(t("brandKnowledge.docs.rescraped", "Re-scraped"));
        } catch (err: any) {
            message.error(
                err?.data?.message ||
                    t("brandKnowledge.docs.scrapeError", "Could not scrape that URL"),
                8
            );
        }
    };

    const handleDelete = async (documentId: number) => {
        try {
            await deleteDoc({ jwtToken, brandId, documentId }).unwrap();
            message.success(t("brandKnowledge.docs.deleted", "Removed"));
        } catch {
            message.error(t("brandKnowledge.docs.deleteError", "Could not remove"));
        }
    };

    const handleDownload = async (documentId: number) => {
        try {
            const { url: signedUrl } = await triggerDownload({
                jwtToken,
                brandId,
                documentId,
            }).unwrap();
            window.open(signedUrl, "_blank", "noopener,noreferrer");
        } catch {
            message.error(
                t("brandKnowledge.docs.downloadError", "Could not download file")
            );
        }
    };

    const columns = [
        {
            title: t("brandKnowledge.docs.col.source", "Source"),
            key: "source",
            render: (_: unknown, doc: IBrandKnowledgeDocument) => (
                <Space>
                    {doc.sourceType === "url_scrape" ? (
                        <GlobalOutlined />
                    ) : (
                        <FileTextOutlined />
                    )}
                    <Text>{doc.sourceRef}</Text>
                </Space>
            ),
        },
        {
            title: t("brandKnowledge.docs.col.type", "Type"),
            key: "type",
            render: (_: unknown, doc: IBrandKnowledgeDocument) => (
                <Tag color={doc.sourceType === "url_scrape" ? "blue" : "geekblue"}>
                    {doc.sourceType === "url_scrape"
                        ? t("brandKnowledge.docs.type.website", "Website")
                        : t("brandKnowledge.docs.type.upload", "Upload")}
                </Tag>
            ),
        },
        {
            title: t("brandKnowledge.docs.col.tokens", "Tokens"),
            dataIndex: "tokenCount",
            key: "tokenCount",
            render: (n: number) => `~${n}`,
        },
        {
            title: (
                <Space size={4}>
                    {t("brandKnowledge.docs.col.inContext", "In AI context")}
                    <Tooltip
                        title={t(
                            "brandKnowledge.docs.inContextHelp",
                            "When on, this document's full text is included in the AI's brand context. Turn it off once you've added its key points to the fields above. That stops the AI seeing the same content twice. The document stays saved either way."
                        )}
                    >
                        <InfoCircleOutlined style={{ color: "#999" }} />
                    </Tooltip>
                </Space>
            ),
            key: "inContext",
            render: (_: unknown, doc: IBrandKnowledgeDocument) => (
                <Switch
                    size="small"
                    checked={doc.includeInContext}
                    onChange={(v) => handleToggleInclude(doc.id, v)}
                />
            ),
        },
        {
            title: "",
            key: "actions",
            align: "right" as const,
            render: (_: unknown, doc: IBrandKnowledgeDocument) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(doc)}
                    >
                        {t("brandKnowledge.docs.edit", "View / edit")}
                    </Button>
                    {doc.hasRawText && (
                        <Button
                            size="small"
                            icon={<ThunderboltOutlined />}
                            onClick={() => handleRedistill(doc.id)}
                        >
                            {t("brandKnowledge.docs.redistill", "Re-clean")}
                        </Button>
                    )}
                    {doc.hasOriginal && (
                        <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownload(doc.id)}
                        >
                            {t("brandKnowledge.docs.download", "Download")}
                        </Button>
                    )}
                    {doc.sourceType === "url_scrape" && (
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => handleRescrape(doc.id)}
                        >
                            {t("brandKnowledge.docs.rescrape", "Re-scrape")}
                        </Button>
                    )}
                    <Popconfirm
                        title={t(
                            "brandKnowledge.docs.confirmDelete",
                            "Remove this item?"
                        )}
                        onConfirm={() => handleDelete(doc.id)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
        <Card style={{ marginTop: 24 }}>
            <Title level={4} style={{ marginTop: 0 }}>
                {t("brandKnowledge.docs.title", "Documents & Websites")}
            </Title>
            <Paragraph type="secondary">
                {t(
                    "brandKnowledge.docs.description",
                    "Upload brand guidelines, strategy docs or personas, or point us at a web page. We extract the text and feed it to the AI alongside your brand context."
                )}
            </Paragraph>

            <Space wrap style={{ marginBottom: 16 }}>
                <Upload
                    accept={ACCEPTED}
                    showUploadList={false}
                    beforeUpload={handleUpload}
                >
                    <Button icon={<UploadOutlined />} loading={isUploading}>
                        {t("brandKnowledge.docs.upload", "Upload document")}
                    </Button>
                </Upload>
                <Space.Compact style={{ width: 420, maxWidth: "100%" }}>
                    <Input
                        placeholder={t(
                            "brandKnowledge.docs.urlPlaceholder",
                            "https://yourbrand.com/about"
                        )}
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onPressEnter={handleAddUrl}
                    />
                    <Button
                        type="primary"
                        icon={<GlobalOutlined />}
                        loading={isScraping}
                        onClick={handleAddUrl}
                    >
                        {t("brandKnowledge.docs.addUrl", "Add website")}
                    </Button>
                </Space.Compact>
            </Space>

            {documents.length > 0 && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={t(
                        "brandKnowledge.docs.inContextBanner",
                        "“In AI context” controls whether each document's text is fed to the AI. If you've used a document's suggestions to fill the fields above, switch it off here to avoid the AI seeing the same information twice. The document stays saved and downloadable."
                    )}
                />
            )}
            <Table
                rowKey="id"
                size="small"
                loading={isLoading}
                columns={columns}
                dataSource={documents}
                pagination={false}
                locale={{
                    emptyText: t(
                        "brandKnowledge.docs.empty",
                        "No documents or websites added yet."
                    ),
                }}
            />
        </Card>

        <Modal
            open={!!editDoc}
            title={t("brandKnowledge.docs.editTitle", "Brand knowledge text")}
            onCancel={() => setEditDoc(null)}
            onOk={saveEdit}
            okText={t("brandKnowledge.docs.save", "Save")}
            confirmLoading={isSavingText}
            width={720}
        >
            <Paragraph type="secondary">
                {t(
                    "brandKnowledge.docs.editHelp",
                    "This is the text fed to the AI for this source. Edit it to remove anything irrelevant. Use “Re-clean” to regenerate it from the original with AI."
                )}
            </Paragraph>
            <TextArea
                rows={16}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                disabled={loadingText}
                style={{ fontFamily: "monospace" }}
            />
        </Modal>
        </>
    );
};

export default BrandKnowledgeDocuments;
